import React, { useState } from 'react';
import { Button, CircleButton, EasyModal, Input, Paginator, Select, Toggle } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import { toast } from 'react-toastify';
import {
  useGetUsersQuery,
  useGetUserRoleQuery,
  useAddUserMutation,
  useDeleteUserMutation,
  useEditUserMutation,
  useGetUserGroupsBulkQuery,
  useAddUserToGroupMutation,
  useDeleteUserFromGroupMutation,
} from '@app/store/api/users.api';
import RubberTable from '@app/components/rubber-table';
import { DictionariesSkeleton } from '@app/pages/dictionaries-page/blocks/dictionaries-skeleton';
import AnalyticsStatus from '@app/components/ui/analytics-status/analytics-status';
import { UserModalForm } from '@app/pages/users/blocks/user-modal-form';
import { OptionItem } from '@app/components/ui/select/select.type';
import { UsersListRequestType } from '@app/interfaces/users';
import useDebounce from '@app/hooks/use-debounce';

const UserPage = () => {
  const { t } = useTranslation('pages.usersPage');

  // eslint-disable-next-line
  const [usersBulkRequestData, setUsersBulkRequestData] = useState<
    Omit<UsersListRequestType, 'filter'>
  >({ offset: 0, limit: 10 });
  const [userFilterData, setUserFilterData] = useState<string>();
  const debouncedValue = useDebounce(userFilterData || '');
  const { data: userBulkData, isLoading } = useGetUsersQuery({
    ...usersBulkRequestData,
    filter: (encodeURI(debouncedValue).split(' ') as unknown as string) || undefined,
  });
  const { data: userRole } = useGetUserRoleQuery();
  const { data: groupsList } = useGetUserGroupsBulkQuery();

  const [addUser] = useAddUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [editUser] = useEditUserMutation();
  const [addUserToGroup] = useAddUserToGroupMutation();
  const [deleteUserFromGroup] = useDeleteUserFromGroupMutation();

  const [isOpenAddUserModal, changeOpenAddUserModal] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [userIdForDelete, changeUserIdForDelete] = useState<string>();
  const [isOpenDeleteModal, changeOpenDeleteModal] = useState<boolean>(false);
  const [currentUserData, setCurrentUserData] = useState<any>(null);
  const [groupFilters, setGroupFilters] = useState('default');

  const getRoleNameById = (roleId: string) => {
    const role = userRole?.roles?.find((role) => role.role_id === roleId);
    return role ? role.description : 'Unknown Role';
  };

  const deleteHandleClick = (id: string) => {
    changeUserIdForDelete(id);
    changeOpenDeleteModal(true);
  };

  const handleDeleteUser = () => {
    if (userIdForDelete) {
      deleteUser(userIdForDelete)
        .unwrap()
        .then(() => {
          toast.success(t('toast.success_delete'));
          changeOpenDeleteModal(false);
        })
        .catch((error) => toast.error(`Ошибка: ${error.message}`));
    }
  };

  function handleMutateUserFromGroup(status: 'DELETE' | 'ADD', user_id: string) {
    status === 'ADD'
      ? addUserToGroup({ user_id, id: groupFilters })
      : deleteUserFromGroup({ user_id, id: groupFilters });
  }

  const editHandleClick = (user: any) => {
    setCurrentUserData(user);
    setIsEditMode(true);
    changeOpenAddUserModal(true);
  };

  const onFormSubmitHandler = (data: {
    username: string;
    role_id: string;
    login: string;
    password?: string;
    enable?: boolean;
    groups?: string[];
  }) => {
    if (isEditMode) {
      editUser({
        ...data,
        id: currentUserData.id.toString(),
        enable: data.enable ?? false,
      })
        .unwrap()
        .then(() => {
          toast.success(t('toast.success_edit'));
          changeOpenAddUserModal(false);
          setIsEditMode(false);
          setCurrentUserData(null);
        })
        .catch((error) => {
          const errorMessage = error.data?.detail || error.error || error.message;
          toast.error(`${t('toast.error_while_edit')} ${errorMessage}`);
        });
    } else {
      addUser(data)
        .unwrap()
        .then(() => {
          toast.success(t('toast.success_add'));
          changeOpenAddUserModal(false);
          setIsEditMode(false);
          setCurrentUserData(null);
        })
        .catch((error) => {
          const errorMessage = error.data?.detail || error.error || error.message;
          toast.error(`${t('toast.error_while_add')} ${errorMessage}`);
        });
    }
  };

  const columns = [
    {
      title: t('table.userName'),
      index: 'nameCol',
      filter: true,
    },
    {
      title: t('table.email'),
      index: 'email',
    },
    {
      title: t('table.role'),
      index: 'role',
    },
    {
      title: t('table.groups'),
      index: 'groups',
    },
    {
      title: 'AD',
      index: 'from_ad',
      maxWidth: 70,
    },
    {
      title: t('table.status'),
      index: 'status',
      maxWidth: 150,
    },
    {
      title: t('table.actions'),
      index: 'actions',
      maxWidth: 120,
    },
  ];

  const filteredArr = userBulkData?.users?.map((item) => ({
    ...item,
    nameCol: (
      <div>
        <h1>{item.username}</h1>
      </div>
    ),
    email: (
      <div>
        <h1>{item.login}</h1>
      </div>
    ),
    role: (
      <div>
        <h1>{getRoleNameById(item.role_id)}</h1>
      </div>
    ),
    status: (
      <div className="max-w-[80px]">
        <AnalyticsStatus status={item?.enable ? 'ONACTIVE' : 'DISABLED'} />
      </div>
    ),
    from_ad: (
      <div className="flex w-full items-center justify-">
        <div
          className={`${item.from_ad ? 'bg-basic_green' : 'bg-basic_red'} rounded-full p-2 w-fit`}
        />
      </div>
    ),
    groups: (
      <div>
        {groupFilters === 'default' ? (
          item.groups.map(({ name }) => name).join(', ')
        ) : (
          <div className="w-full items-center justify-start flex">
            <Toggle
              checked={item.groups.map(({ group_id }) => group_id).includes(groupFilters)}
              onChecked={(value) => {
                const key: 'DELETE' | 'ADD' = value ? 'ADD' : 'DELETE';
                handleMutateUserFromGroup(key, item.id);
              }}
            />
          </div>
        )}
      </div>
    ),
    actions: (
      <div className="border-l-[1px] pl-[10px]">
        <CircleButton
          hintTitle={t('table.edit')}
          icon="SettingIcon"
          size={19}
          onClick={() => editHandleClick(item)}
        />
        <CircleButton
          hintTitle={t('table.delete')}
          icon="TrashIcon"
          className="text-basic_red ml-[20px]"
          onClick={() => deleteHandleClick(item.id)}
          disable={!item.can_be_deleted}
          size={19}
        />
      </div>
    ),
  }));

  const renderModal = () => {
    if (!isOpenAddUserModal || !userRole) return null;
    return (
      <UserModalForm
        userRole={userRole}
        isOpenAddUserModal={isOpenAddUserModal}
        changeOpenAddUserModal={changeOpenAddUserModal}
        onFormSubmit={onFormSubmitHandler}
        isEditMode={isEditMode}
        currentUserData={currentUserData}
        groupsList={groupsList?.groups || []}
      />
    );
  };
  function handleChangePagination({ limit, offset }) {
    setUsersBulkRequestData((prev) => ({ ...prev, limit, offset }));
  }
  const groupsOptions: OptionItem[] =
    groupsList?.groups?.map((item) => ({
      title: item.name,
      value: item.id,
    })) || [];
  if (isLoading) return <DictionariesSkeleton />;
  return (
    <div>
      <div className="flex w-full justify-between py-[30px]">
        <div className="flex items-center gap-[10px]">
          <div className="w-[300px]">
            <Input
              defaultValue={userFilterData}
              onChange={(event) => {
                setUserFilterData(event.target.value);
              }}
              icon="SearchIcon"
              placeholder={t('user_search_placeholder')}
            />
          </div>
          <div className="flex items-center gap-[5px]">
            <span>{t('show_groups_desc')}</span>
            <div className="w-[300px]">
              <Select
                options={[{ value: 'default', title: 'all' }, ...groupsOptions]}
                defaultValue={groupFilters}
                onChange={(value) => setGroupFilters(value as string)}
              />
            </div>
          </div>
        </div>
        <Button
          data-id="new-user"
          label={t('add_user_button')}
          onClick={() => {
            changeOpenAddUserModal(true);
            setIsEditMode(false);
            setCurrentUserData(null);
          }}
          icon="CirclePlusIcon"
        />
      </div>
      <div className="flex flex-col gap-[10px]">
        <RubberTable
          onFilter={(filter) => {
            const sort = {
              sortBy: Object.keys(filter)[0],
              sortDesc: Object.keys(filter)[0] ? 0 < Object.values(filter)[0] : undefined,
            };
            setUsersBulkRequestData((prev) => ({ ...prev, ...sort }));
          }}
          columns={columns}
          dataSource={filteredArr || []}
        />
        <Paginator
          limit={usersBulkRequestData?.limit}
          page={Math.ceil((usersBulkRequestData?.offset || 0) + 1)}
          count={userBulkData?.total || 100}
          onChange={handleChangePagination}
        />
      </div>
      {renderModal()}
      <EasyModal
        withoutFooter
        onClose={changeOpenDeleteModal.bind(null, false)}
        label={t('warning')}
        show={isOpenDeleteModal}
        variant="removal"
        onRemove={handleDeleteUser}
      >
        <div className="flex items-center justify-center text-center">
          {userBulkData?.users.find((user) => user.id === userIdForDelete)?.from_ad &&
            t('delete_user_text_ad_warning')}
          {t('delete_user_text')}
        </div>
      </EasyModal>
    </div>
  );
};

export default UserPage;
